/**
 * The household's own list of what it buys.
 *
 * Everything adding used to ask for lives here instead: what a food is called, where it is
 * kept, and how long it lasts. Moving those questions here is what makes adding a single tap,
 * and it puts each answer somewhere a person can correct it at leisure rather than in front of
 * a bag of shopping.
 *
 * A row opens into its own editor rather than a screen of its own. The list is the context —
 * the point of editing "how long milk lasts" is usually that the milk beside it lasts longer.
 */

import { useState } from 'preact/hooks';

import type { Actions, Undoable } from '../actions';
import type { Grocery, Location } from '../api/types';
import { needsALook } from '../domain/groceries';
import { LookingUp } from './waiting';

interface Props {
  groceries: Grocery[];
  locations: Location[];
  actions: Actions;
  onChanged: (undo: Undoable | null) => void;
}

export function Groceries({ groceries, locations, actions, onChanged }: Props) {
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState('');
  // The name currently being looked up, or nothing. Held separately from the field, which is
  // cleared the moment the answer lands.
  const [lookingUp, setLookingUp] = useState<string | null>(null);

  /**
   * Puts a new food on the list.
   *
   * A food the catalog knows is instant; anything else is asked about over the network and can
   * take several seconds, so the wait is said out loud and the button that started it is shut
   * until it is over. A second press would be a second paid call for the same food.
   */
  const add = async (): Promise<void> => {
    const wanted = adding.trim();
    if (wanted === '' || lookingUp !== null) {
      return;
    }

    setLookingUp(wanted);
    try {
      // Guessed rather than asked about: the entry appears filled in, and the row it appears
      // as is itself the place to correct anything the guess got wrong.
      const listed = await actions.listGrocery(wanted);
      setAdding('');
      onChanged(null);
      setEditing(listed?.slug ?? null);
    } finally {
      setLookingUp(null);
    }
  };

  const save = async (grocery: Grocery): Promise<void> => {
    // Saving usually changes nothing but this row. Pointing the entry somewhere new also moves
    // what it has already placed, which is the one edit here worth handing back a way out of.
    const undo = await actions.saveGrocery(grocery);
    setEditing(null);
    onChanged(undo);
  };

  const remove = async (grocery: Grocery): Promise<void> => {
    const undo = await actions.removeGrocery(grocery);
    setEditing(null);
    onChanged(undo);
  };

  return (
    <div class="groceries">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void add();
        }}
      >
        <input
          type="text"
          value={adding}
          placeholder="Something you buy"
          enterkeyhint="done"
          onInput={(event) => setAdding((event.target as HTMLInputElement).value)}
        />
        <button
          type="submit"
          disabled={lookingUp !== null || adding.trim() === '' || locations.length === 0}
        >
          Add
        </button>
      </form>

      {lookingUp === null ? null : <LookingUp name={lookingUp} />}

      {unplaceable(groceries) > 0 ? (
        <p class="note asking">
          {unplaceable(groceries) === 1
            ? "There's 1 thing here nobody could place."
            : `There are ${unplaceable(groceries)} things here nobody could place.`}{' '}
          Tap to say where it goes and how long it keeps.
        </p>
      ) : null}

      {groceries.length === 0 ? (
        <p class="empty">
          Nothing here yet. What you add turns up by itself as you shop — this is where you say
          where each thing goes and how long it keeps.
        </p>
      ) : (
        <ul class="list">
          {[...groceries].sort(byWhatNeedsAttention).map((grocery) =>
            grocery.slug === editing ? (
              <li key={grocery.slug}>
                <Editor
                  grocery={grocery}
                  locations={locations}
                  onSave={(edited) => void save(edited)}
                  onRemove={() => void remove(grocery)}
                  onCancel={() => setEditing(null)}
                />
              </li>
            ) : (
              <li key={grocery.slug}>
                <button
                  type="button"
                  class={needsALook(grocery) ? 'grocery unplaceable' : 'grocery'}
                  onClick={() => setEditing(grocery.slug)}
                >
                  <span class="name">{grocery.name}</span>
                  {needsALook(grocery) ? (
                    // Said plainly rather than left as a blank date. A guess the app cannot
                    // vouch for has to look different from one it can, or the first wrong
                    // date is the last time anybody believes the right ones.
                    <span class="unsure">needs a look</span>
                  ) : (
                    <>
                      <span class="where">{placeName(locations, grocery.location_id)}</span>
                      <span class="keeps">{describeShelfLife(grocery.shelf_life_days)}</span>
                    </>
                  )}
                </button>
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * One entry, open for editing.
 *
 * The shelf-life fields are free to be left empty, and empty means something definite: it does
 * not go off. That is how a staple is said here — one field rather than a second concept, and
 * nothing the user has to classify.
 */
export function Editor({
  grocery,
  locations,
  onSave,
  onRemove,
  onCancel,
}: {
  grocery: Grocery;
  locations: Location[];
  onSave: (edited: Grocery) => void;
  onRemove: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(grocery.name);
  const [locationId, setLocationId] = useState(grocery.location_id);
  const [keeps, setKeeps] = useState(asField(grocery.shelf_life_days));
  const [opened, setOpened] = useState(asField(grocery.opened_shelf_life_days));

  return (
    <form
      class="editor"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          ...grocery,
          name: name.trim(),
          location_id: locationId,
          shelf_life_days: asDays(keeps),
          opened_shelf_life_days: asDays(opened),
        });
      }}
    >
      <label>
        Called
        <input
          type="text"
          value={name}
          onInput={(event) => setName((event.target as HTMLInputElement).value)}
        />
      </label>

      <label>
        Kept in
        <select
          value={locationId}
          onChange={(event) => setLocationId((event.target as HTMLSelectElement).value)}
        >
          {locations.map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </select>
      </label>

      <label>
        Lasts
        <input
          type="number"
          inputmode="numeric"
          min="1"
          value={keeps}
          placeholder="doesn't go off"
          onInput={(event) => setKeeps((event.target as HTMLInputElement).value)}
        />
        <span class="unit">days</span>
      </label>

      <label>
        Once opened
        <input
          type="number"
          inputmode="numeric"
          min="1"
          value={opened}
          placeholder="same"
          onInput={(event) => setOpened((event.target as HTMLInputElement).value)}
        />
        <span class="unit">days</span>
      </label>

      <div class="editor-actions">
        <button type="submit" class="primary" disabled={name.trim() === ''}>
          Save
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" class="remove" onClick={onRemove}>
          Stop buying
        </button>
      </div>
    </form>
  );
}

/** How many entries nobody could place. */
function unplaceable(groceries: Grocery[]): number {
  return groceries.filter(needsALook).length;
}

/**
 * What needs attention first, then alphabetical.
 *
 * The rows nobody could place are the only ones with anything to do on this screen, and a list
 * of forty things is one nobody scrolls looking for them.
 */
function byWhatNeedsAttention(left: Grocery, right: Grocery): number {
  if (needsALook(left) !== needsALook(right)) {
    return needsALook(left) ? -1 : 1;
  }

  return left.name.localeCompare(right.name);
}

/** How long it keeps, phrased for a list rather than a form. */
export function describeShelfLife(days: number | null): string {
  if (days === null) {
    return 'keeps';
  }
  if (days === 1) {
    return 'a day';
  }
  if (days < 14) {
    return `${days} days`;
  }
  if (days < 60) {
    return `${Math.round(days / 7)} weeks`;
  }

  return `${Math.round(days / 30)} months`;
}

/** What to call the place a food lives, wherever the app has to name one. */
export function placeName(locations: Location[], locationId: string): string {
  return locations.find((location) => location.id === locationId)?.name ?? 'somewhere';
}

function asField(days: number | null): string {
  return days === null ? '' : String(days);
}

/** An empty or unreadable field means "it does not go off", not zero days. */
function asDays(value: string): number | null {
  const days = Number.parseInt(value, 10);

  return Number.isFinite(days) && days > 0 ? days : null;
}
